import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { adminPaymentsService, type ProviderLog, type ProviderLogsFilters } from '@/services/adminPayments';
import { useToast } from '@/hooks/use-toast';
import { useRolePermissions } from '@/hooks/use-role-permissions';
import { Filter, ChevronLeft, ChevronRight, Eye, Globe, Clock, AlertCircle, CheckCircle, XCircle } from 'lucide-react';
import { format } from 'date-fns';

export default function PaymentsLogsPage() {
  const { t } = useTranslation();
  const { canView } = useRolePermissions();
  const canViewPayments = canView('payments');
  const { toast } = useToast();

  const [filters, setFilters] = useState<ProviderLogsFilters>({ page: 1, limit: 10 });
  const [loading, setLoading] = useState(false);
  const [logs, setLogs] = useState<ProviderLog[]>([]);
  const [pageInfo, setPageInfo] = useState({ page: 1, pages: 1, total: 0, limit: 10 });
  const [selectedLog, setSelectedLog] = useState<ProviderLog | null>(null);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);

  const loadLogs = async () => {
    try {
      setLoading(true);
      const res = await adminPaymentsService.getProviderLogs(filters);
      if (res.success && res.data) {
        setLogs(res.data.logs || []);
        setPageInfo(res.data.pagination || { page: 1, pages: 1, total: 0, limit: filters.limit || 10 });
      } else {
        setLogs([]);
        setPageInfo({ page: 1, pages: 1, total: 0, limit: filters.limit || 10 });
      }
    } catch (e: any) {
      console.error('Failed to load logs:', e);
      setLogs([]);
      setPageInfo({ page: 1, pages: 1, total: 0, limit: filters.limit || 10 });
      toast({
        title: t('error'),
        description: e?.response?.data?.message || e?.message || t('failedToLoadLogs'),
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadLogs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.page, filters.limit, filters.provider_code, filters.payment_method_id, filters.request_type, filters.status_code, filters.level, filters.created_from, filters.created_to, filters.search]);

  if (!canViewPayments) {
    return <div className="p-6 text-center text-muted-foreground">{t('youDoNotHavePermission')}</div>;
  }

  return (
    <div className="p-4 md:p-6 space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <Filter className="h-5 w-5" />
            <span>{t('filters')}</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <Input
              placeholder={t('searchLogs') || 'Search (URL, ID, etc.)'}
              value={filters.search || ''}
              onChange={(e) => {
                const value = e.target.value.trim();
                setFilters((f) => ({ ...f, search: value || undefined, page: 1 }));
              }}
            />
            <Input
              placeholder={t('filterByProvider') || 'Provider Code'}
              value={filters.provider_code || ''}
              onChange={(e) => {
                const value = e.target.value.trim();
                setFilters((f) => ({ ...f, provider_code: value || undefined, page: 1 }));
              }}
            />
            <Input
              placeholder={t('paymentMethodId') || 'Payment Method ID'}
              value={filters.payment_method_id || ''}
              onChange={(e) => {
                const value = e.target.value.trim();
                setFilters((f) => ({ ...f, payment_method_id: value || undefined, page: 1 }));
              }}
            />
            <Select
              value={filters.request_type || 'all'}
              onValueChange={(value) => {
                setFilters((f) => {
                  const newFilters = { ...f, page: 1 };
                  if (value === 'all') {
                    delete newFilters.request_type;
                  } else {
                    newFilters.request_type = value;
                  }
                  return newFilters;
                });
              }}
            >
              <SelectTrigger>
                <SelectValue placeholder={t('requestType') || 'Request Type'} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t('allRequestTypes') || 'All Types'}</SelectItem>
                <SelectItem value="GET">GET</SelectItem>
                <SelectItem value="POST">POST</SelectItem>
                <SelectItem value="PUT">PUT</SelectItem>
                <SelectItem value="DELETE">DELETE</SelectItem>
                <SelectItem value="PATCH">PATCH</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mt-4">
            <Select
              value={
                filters.status_code === undefined || filters.status_code === null
                  ? 'all'
                  : typeof filters.status_code === 'string'
                  ? filters.status_code
                  : filters.status_code.toString()
              }
              onValueChange={(value) => {
                setFilters((f) => {
                  const newFilters = { ...f, page: 1 };
                  if (value === 'all') {
                    delete newFilters.status_code;
                  } else if (value === 'success') {
                    newFilters.status_code = '2xx';
                  } else if (value === 'error') {
                    newFilters.status_code = '4xx';
                  } else {
                    newFilters.status_code = Number(value);
                  }
                  return newFilters;
                });
              }}
            >
              <SelectTrigger>
                <SelectValue placeholder={t('statusCode') || 'Status Code'} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t('allStatusCodes') || 'All Status Codes'}</SelectItem>
                <SelectItem value="success">{t('success') || 'Success (2xx)'}</SelectItem>
                <SelectItem value="200">200 OK</SelectItem>
                <SelectItem value="201">201 Created</SelectItem>
                <SelectItem value="400">400 Bad Request</SelectItem>
                <SelectItem value="401">401 Unauthorized</SelectItem>
                <SelectItem value="404">404 Not Found</SelectItem>
                <SelectItem value="500">500 Server Error</SelectItem>
                <SelectItem value="error">{t('error') || 'Error (4xx/5xx)'}</SelectItem>
              </SelectContent>
            </Select>
            <Select
              value={filters.level || 'all'}
              onValueChange={(value) => {
                setFilters((f) => {
                  const newFilters = { ...f, page: 1 };
                  if (value === 'all') {
                    delete newFilters.level;
                  } else {
                    newFilters.level = value as any;
                  }
                  return newFilters;
                });
              }}
            >
              <SelectTrigger>
                <SelectValue placeholder={t('logLevel') || 'Log Level'} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t('allLevels') || 'All Levels'}</SelectItem>
                <SelectItem value="debug">{t('debug') || 'Debug'}</SelectItem>
                <SelectItem value="info">{t('info') || 'Info'}</SelectItem>
                <SelectItem value="warn">{t('warning') || 'Warning'}</SelectItem>
                <SelectItem value="error">{t('error') || 'Error'}</SelectItem>
              </SelectContent>
            </Select>
            <div>
              <label className="text-sm font-medium text-muted-foreground mb-1 block">{t('dateFrom') || 'From'}</label>
              <Input
                type="date"
                value={filters.created_from || ''}
                onChange={(e) => {
                  const value = e.target.value.trim();
                  setFilters((f) => ({ ...f, created_from: value || undefined, page: 1 }));
                }}
              />
            </div>
            <div>
              <label className="text-sm font-medium text-muted-foreground mb-1 block">{t('dateTo') || 'To'}</label>
              <Input
                type="date"
                value={filters.created_to || ''}
                onChange={(e) => {
                  const value = e.target.value.trim();
                  setFilters((f) => ({ ...f, created_to: value || undefined, page: 1 }));
                }}
              />
            </div>
          </div>
          <div className="flex justify-between items-center mt-4">
            <Button
              variant="outline"
              onClick={() => {
                const currentLimit = filters.limit || 10;
                setFilters({ page: 1, limit: currentLimit });
              }}
            >
              {t('clearFilters')}
            </Button>
            <div className="flex items-center space-x-2">
              <span className="text-sm text-muted-foreground">{t('itemsPerPage') || 'Items per page'}</span>
              <Select
                value={(filters.limit || 10).toString()}
                onValueChange={(v) => setFilters((f) => ({ ...f, limit: parseInt(v), page: 1 }))}
              >
                <SelectTrigger className="w-[100px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="10">10</SelectItem>
                  <SelectItem value="25">25</SelectItem>
                  <SelectItem value="50">50</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="flex justify-between items-center mt-4">
            <Button
              variant="outline"
              onClick={() => setFilters({ page: 1, limit: filters.limit || 10 })}
            >
              {t('clearFilters')}
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>{t('providerLogs')}</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <div className="text-muted-foreground">{t('loadingLogs')}</div>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t('requestType') || 'Method'}</TableHead>
                  <TableHead>{t('requestUrl') || 'URL'}</TableHead>
                  <TableHead>{t('statusCode') || 'Status'}</TableHead>
                  <TableHead>{t('executionTime') || 'Time'}</TableHead>
                  <TableHead>{t('createdAt') || 'Date'}</TableHead>
                  <TableHead className="text-right">{t('actions') || 'Actions'}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {logs.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center py-8">{t('noLogsFound')}</TableCell>
                  </TableRow>
                ) : (
                  logs.map((log) => {
                    const shortId = log.id.length > 12 ? `${log.id.substring(0, 12)}...` : log.id;
                    const shortUrl = log.request_url ? (log.request_url.length > 50 ? `${log.request_url.substring(0, 50)}...` : log.request_url) : '-';
                    const statusCode = log.status_code || 0;
                    const isSuccess = statusCode >= 200 && statusCode < 300;
                    const isError = statusCode >= 400;

                    return (
                      <TableRow key={log.id}>
                        <TableCell>
                          <Badge variant={log.request_type === 'POST' ? 'default' : 'outline'}>
                            {log.request_type || '-'}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center space-x-2 max-w-[400px]">
                            <Globe className="h-3 w-3 text-muted-foreground flex-shrink-0" />
                            <span className="truncate font-mono text-xs" title={log.request_url}>
                              {shortUrl}
                            </span>
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge 
                            variant={
                              isSuccess ? 'default' :
                              isError ? 'destructive' :
                              'secondary'
                            }
                            className="flex items-center gap-1 w-fit"
                          >
                            {isSuccess && <CheckCircle className="h-3 w-3" />}
                            {isError && <XCircle className="h-3 w-3" />}
                            {statusCode || '-'}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          {log.execution_time_ms !== undefined ? (
                            <div className="flex items-center space-x-1 text-sm">
                              <Clock className="h-3 w-3 text-muted-foreground" />
                              <span>{log.execution_time_ms}ms</span>
                            </div>
                          ) : '-'}
                        </TableCell>
                        <TableCell>
                          {log.createdAt ? format(new Date(log.createdAt), 'yyyy-MM-dd HH:mm:ss') : '-'}
                        </TableCell>
                        <TableCell className="text-right">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              setSelectedLog(log);
                              setIsViewModalOpen(true);
                            }}
                            title={t('viewDetails') || 'View Details'}
                            className="h-8 w-8 p-0"
                          >
                            <Eye className="h-4 w-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          )}

          {!loading && pageInfo && pageInfo.total > 0 && (
            <div className="flex items-center justify-between space-x-2 py-4">
              <div className="text-sm text-muted-foreground">
                {t("showing")} {((pageInfo.page - 1) * pageInfo.limit) + 1} - {Math.min(pageInfo.page * pageInfo.limit, pageInfo.total)} {t("of")} {pageInfo.total} {t("results")}
              </div>
              
              {pageInfo.pages > 1 && (
                <div className="flex space-x-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setFilters((f) => ({ ...f, page: Math.max(1, (f.page || 1) - 1) }))}
                    disabled={(filters.page || 1) <= 1}
                  >
                    {t("previous")}
                  </Button>
                  
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setFilters((f) => ({ ...f, page: Math.min(pageInfo.pages, (f.page || 1) + 1) }))}
                    disabled={(filters.page || 1) >= pageInfo.pages}
                  >
                    {t("next")}
                  </Button>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Log Details Modal */}
      <Dialog open={isViewModalOpen} onOpenChange={setIsViewModalOpen}>
        <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{t('logDetails') || 'Log Details'}</DialogTitle>
            <DialogDescription>
              {t('viewAllLogInformation') || 'View all log information including request and response data'}
            </DialogDescription>
          </DialogHeader>
          {selectedLog && (
            <LogDetailsView log={selectedLog} />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

// Log Details View Component
function LogDetailsView({ log }: { log: ProviderLog }) {
  const { t } = useTranslation();

  const formatJSON = (jsonString: string | null | undefined) => {
    if (!jsonString) return null;
    try {
      const parsed = typeof jsonString === 'string' ? JSON.parse(jsonString) : jsonString;
      return JSON.stringify(parsed, null, 2);
    } catch {
      return jsonString;
    }
  };

  const statusCode = log.status_code || 0;
  const isSuccess = statusCode >= 200 && statusCode < 300;
  const isError = statusCode >= 400;
  const requestBody = formatJSON(log.request_body);
  const responseBody = formatJSON(log.response_body);

  return (
    <div className="space-y-6">
      {/* Basic Information */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">{t('basicInformation') || 'Basic Information'}</CardTitle>
        </CardHeader>
        <CardContent className="grid grid-cols-2 gap-4">
          <div>
            <label className="text-sm font-medium text-muted-foreground">{t('logId') || 'Log ID'}</label>
            <p className="font-mono text-sm">{log.id}</p>
          </div>
          {log.payment_method_id && (
            <div>
              <label className="text-sm font-medium text-muted-foreground">{t('paymentMethodId') || 'Payment Method ID'}</label>
              <p className="font-mono text-sm">{log.payment_method_id}</p>
            </div>
          )}
          <div>
            <label className="text-sm font-medium text-muted-foreground">{t('requestType') || 'Request Type'}</label>
            <div className="mt-1">
              <Badge variant={log.request_type === 'POST' ? 'default' : 'outline'}>
                {log.request_type || '-'}
              </Badge>
            </div>
          </div>
          <div>
            <label className="text-sm font-medium text-muted-foreground">{t('statusCode') || 'Status Code'}</label>
            <div className="mt-1">
              <Badge 
                variant={
                  isSuccess ? 'default' :
                  isError ? 'destructive' :
                  'secondary'
                }
                className="flex items-center gap-1 w-fit"
              >
                {isSuccess && <CheckCircle className="h-3 w-3" />}
                {isError && <XCircle className="h-3 w-3" />}
                {statusCode || '-'}
              </Badge>
            </div>
          </div>
          {log.execution_time_ms !== undefined && (
            <div>
              <label className="text-sm font-medium text-muted-foreground">{t('executionTime') || 'Execution Time'}</label>
              <p className="flex items-center gap-1">
                <Clock className="h-3 w-3" />
                {log.execution_time_ms}ms
              </p>
            </div>
          )}
          <div>
            <label className="text-sm font-medium text-muted-foreground">{t('createdAt') || 'Created At'}</label>
            <p className="text-sm">{log.createdAt ? format(new Date(log.createdAt), 'yyyy-MM-dd HH:mm:ss') : '-'}</p>
          </div>
          {log.error_message && (
            <div className="col-span-2">
              <label className="text-sm font-medium text-muted-foreground">{t('errorMessage') || 'Error Message'}</label>
              <p className="text-sm text-destructive mt-1 flex items-center gap-1">
                <AlertCircle className="h-4 w-4" />
                {log.error_message}
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Request URL */}
      {log.request_url && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">{t('requestUrl') || 'Request URL'}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center space-x-2 p-3 bg-muted rounded-md">
              <Globe className="h-4 w-4 text-muted-foreground flex-shrink-0" />
              <code className="text-sm break-all">{log.request_url}</code>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Request Body */}
      {requestBody && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">{t('requestBody') || 'Request Body'}</CardTitle>
          </CardHeader>
          <CardContent>
            <pre className="bg-muted p-4 rounded-md text-xs overflow-x-auto max-h-96 overflow-y-auto">
              {requestBody}
            </pre>
          </CardContent>
        </Card>
      )}

      {/* Response Body */}
      {responseBody && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">{t('responseBody') || 'Response Body'}</CardTitle>
          </CardHeader>
          <CardContent>
            <pre className={`p-4 rounded-md text-xs overflow-x-auto max-h-96 overflow-y-auto ${
              isError ? 'bg-destructive/10' : 'bg-muted'
            }`}>
              {responseBody}
            </pre>
          </CardContent>
        </Card>
      )}

      {/* Context (if available) */}
      {log.context && Object.keys(log.context).length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">{t('context') || 'Context'}</CardTitle>
          </CardHeader>
          <CardContent>
            <pre className="bg-muted p-4 rounded-md text-xs overflow-x-auto max-h-96 overflow-y-auto">
              {JSON.stringify(log.context, null, 2)}
            </pre>
          </CardContent>
        </Card>
      )}
    </div>
  );
}


